// C.1 — Главврач Ариана (livlife_ariana).
//
// xlsx v6 dsl_trigger1 (раз в 2 хода — на чётных turn'ах):
//   event: turn_start
//   target: ally_lowest_hp
//   effect: composite
//   value: return_to_hand:keep_buffs_full_restore@ally_lowest_hp
//          | summon:from_hand_unit_card@own_line
//   condition: turn_number_even_and_hand_not_full_and_has_unit_in_hand
//
// Поведение (с балансером):
//   1) Триггер в начале своего хода. Чётный turn (2, 4, 6, ...).
//   2) Picker раненого союзника (если их несколько — UI выбирает; если один —
//      авто). Юнит с full hp = не раненый, не кандидат. Если раненых нет —
//      триггер НЕ срабатывает вовсе.
//   3) Picker карты юнита из руки (не считая ту что забирается с поля).
//      Если в руке только Действия (юнитов нет) — возврат произошёл,
//      постановка отменяется. Если рука ПОЛНАЯ (10 карт) — триггер НЕ
//      срабатывает вовсе.
//   4) Возвращённый юнит сохраняет permanent-баффы и keyword'ы, hp/armor
//      восстанавливаются до full.
//   5) Выбранный юнит из руки ставится на свободную ячейку (на любую линию
//      по выбору игрока; пока — на ту же линию что освободилась).
//
// xlsx v6 dsl_active (Ф2, 1× за матч):
//   target: all_empty_slots
//   effect: fill_line_with_summon
//   value: livlife_medtekh_ll1
//   → Заполнить все свободные ячейки Медтехом LL-1.

import { registerImplementedAbility } from '../../warn-unimplemented.js';
import { evacuateUnitToHand } from '../evacuate.js';
import { summonUnit } from '../../global/summon.js';
import { onUnitSummoned } from '../../deus/hooks.js';

export const HERO_ID = 'livlife_ariana';
const MEDTEKH_ID = 'livlife_medtekh_ll1';

// === Trigger состояние ===
// Если триггеру нужен picker — выставляем флаг pending. UI/бот разрешает
// его через `applyArianaTriggerChoice(state, playerIdx, opts)`.
//
// state.players[idx].arianaTrigger = {
//   stage: 'wounded' | 'place',
//   woundedInstanceId: null | <id>,    — выбран на стадии wounded
//   freedSlot: { line, idx } | null,   — освобождённая ячейка
// }
// Бот при отсутствии UI вызывает picker автоматически (первый раненый,
// первая юнит-карта в руке).

function getWoundedAllies(player) {
  const result = [];
  if (!player?.field) return result;
  for (let line = 0; line < 2; line++) {
    const row = player.field[line] || [];
    for (let s = 0; s < row.length; s++) {
      const u = row[s];
      if (!u) continue;
      if (!u.isAlive || !u.isAlive()) continue;
      const cur = u.current?.hp ?? 0;
      const max = u.base?.hp ?? cur;
      if (cur < max) result.push({ unit: u, line, idx: s });
    }
  }
  // Сортировка по убыванию ранения (max-cur).
  result.sort((a, b) => {
    const da = (a.unit.base?.hp ?? 0) - (a.unit.current?.hp ?? 0);
    const db = (b.unit.base?.hp ?? 0) - (b.unit.current?.hp ?? 0);
    return db - da;
  });
  return result;
}

function getUnitCardsInHand(player) {
  return (player?.hand || []).filter(c => c?.type === 'unit');
}

// Запустить триггер в начале хода. Если условия не соблюдены — no-op.
// Иначе выставляет state.players[idx].arianaTrigger в режим 'wounded'
// и возвращает { ok: true, needsPicker: true, candidates }.
//
// Если кандидат раненых ровно один — автоматически переходит на стадию
// 'place', возвращая candidates юнитов из руки.
//
// Если бот (нет UI) — UI вызывает applyArianaTriggerChoice сам.
export function arianaOnTurnStart(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.hero || p.hero.id !== HERO_ID) return { ok: false, reason: 'wrong_hero' };
  if (p.hero.current?.silenced) return { ok: false, reason: 'silenced' };

  const turn = state.currentTurn ?? 0;
  if (turn < 2 || turn % 2 !== 0) return { ok: false, reason: 'wrong_turn' };

  // Условие: рука НЕ полная.
  const HAND_MAX = 10;
  if ((p.hand?.length ?? 0) >= HAND_MAX) {
    state.log?.add(`🩺 Ариана [${p.name}]: триггер пропущен — рука полная`);
    return { ok: false, reason: 'hand_full' };
  }
  // Условие: есть раненый союзник.
  const wounded = getWoundedAllies(p);
  if (wounded.length === 0) {
    return { ok: false, reason: 'no_wounded' };
  }
  // Условие: в руке есть карта юнита.
  const unitsInHand = getUnitCardsInHand(p);
  if (unitsInHand.length === 0) {
    state.log?.add(`🩺 Ариана [${p.name}]: триггер пропущен — в руке нет юнитов`);
    return { ok: false, reason: 'no_unit_in_hand' };
  }

  // Стартуем picker «выбор раненого».
  p.arianaTrigger = {
    stage: 'wounded',
    candidates: wounded.map(w => w.unit.instanceId),
    woundedInstanceId: null,
    freedSlot: null
  };
  if (state.log) {
    state.log.add(`🩺 Ариана [${p.name}]: триггер — выбери раненого союзника`);
  }
  return { ok: true, needsPicker: true, stage: 'wounded', candidates: wounded };
}

// UI/бот применяет выбор раненого.
export function applyArianaTriggerWoundedChoice(state, playerIdx, woundedInstanceId) {
  const p = state.players?.[playerIdx];
  if (!p?.arianaTrigger) return { ok: false, reason: 'no_pending_trigger' };
  if (p.arianaTrigger.stage !== 'wounded') return { ok: false, reason: 'wrong_stage' };

  // Найти юнита.
  let chosen = null;
  let chosenSlot = null;
  for (let line = 0; line < 2; line++) {
    const row = p.field[line] || [];
    for (let s = 0; s < row.length; s++) {
      const u = row[s];
      if (u && u.instanceId === woundedInstanceId) {
        chosen = u;
        chosenSlot = { line, idx: s };
        break;
      }
    }
    if (chosen) break;
  }
  if (!chosen) return { ok: false, reason: 'wounded_not_on_field' };

  // Возврат с full restore + сохранение баффов. evacuateUnitToHand уже
  // сохраняет permanent-баффы и keyword'ы. Однако hp не восстанавливается
  // до full автоматически. Поправим вручную ПОСЛЕ.
  const result = evacuateUnitToHand(state, playerIdx, chosen, 'Ариана');
  if (!result.ok) return { ok: false, reason: 'evacuate_failed' };

  // После эвакуации карта в руке. Найти её и восстановить hp/armor.
  const inHand = p.hand?.find(c => c.instanceId === chosen.instanceId);
  if (inHand?.current) {
    inHand.current.hp = inHand.base?.hp ?? inHand.current.hp;
    inHand.current.armor = (typeof inHand.maxArmorWithMods === 'number')
      ? inHand.maxArmorWithMods
      : (inHand.base?.armor ?? 0);
  }

  // Переходим на стадию 'place'.
  p.arianaTrigger.stage = 'place';
  p.arianaTrigger.woundedInstanceId = chosen.instanceId;
  p.arianaTrigger.freedSlot = chosenSlot;

  // Если в руке нет юнитов (только Действия) — постановка отменяется,
  // триггер завершается успехом (возврат произошёл).
  const unitsInHand = getUnitCardsInHand(p);
  if (unitsInHand.length === 0) {
    state.log?.add(`🩺 Ариана: возврат произошёл, но в руке только Действия — постановка отменена`);
    p.arianaTrigger = null;
    return { ok: true, completed: true };
  }

  if (state.log) {
    state.log.add(`🩺 Ариана: выбери юнит из руки для размещения на освобождённой ячейке`);
  }
  return { ok: true, needsPicker: true, stage: 'place', candidates: unitsInHand, freedSlot: chosenSlot };
}

// UI/бот применяет выбор юнита из руки + ячейки для размещения.
//   opts.handCardInstanceId — какой юнит из руки ставим.
//   opts.placementSlot — { line, idx } куда ставим (по умолчанию — освобождённая ячейка).
export function applyArianaTriggerPlaceChoice(state, playerIdx, opts = {}) {
  const p = state.players?.[playerIdx];
  if (!p?.arianaTrigger) return { ok: false, reason: 'no_pending_trigger' };
  if (p.arianaTrigger.stage !== 'place') return { ok: false, reason: 'wrong_stage' };

  const handCardInstanceId = opts.handCardInstanceId;
  if (handCardInstanceId == null) return { ok: false, reason: 'no_hand_card' };

  const handIdx = p.hand.findIndex(c => c.instanceId === handCardInstanceId);
  if (handIdx < 0) return { ok: false, reason: 'hand_card_not_found' };
  const card = p.hand[handIdx];
  if (card.type !== 'unit') return { ok: false, reason: 'not_unit_card' };

  const slot = opts.placementSlot ?? p.arianaTrigger.freedSlot;
  if (!slot || !p.field?.[slot.line] || p.field[slot.line][slot.idx] !== null) {
    return { ok: false, reason: 'slot_unavailable' };
  }

  // Извлечь из руки, поставить через placeCard.
  p.hand.splice(handIdx, 1);
  try {
    p.placeCard(card, slot.line, slot.idx);
  } catch {
    return { ok: false, reason: 'place_failed' };
  }
  card.placedTurn = state.currentTurn;
  card.revealed = true;
  // Триггер onUnitSummoned для всех слушателей (Передовая медицина, Подкрепление, и т.п.).
  try { onUnitSummoned(state, playerIdx, card); } catch (e) { console.warn(e); }

  p.arianaTrigger = null;
  state.log?.add(`🩺 Ариана: ${card.name_ru} размещён на (${slot.line + 1}, ${slot.idx + 1})`);
  return { ok: true, completed: true };
}

// Авто-разрешение для бота (нет UI — берём первого раненого, первый юнит из руки).
export function autoResolveArianaTrigger(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.arianaTrigger) return;
  if (p.arianaTrigger.stage === 'wounded') {
    const candidates = p.arianaTrigger.candidates || [];
    if (candidates.length === 0) { p.arianaTrigger = null; return; }
    applyArianaTriggerWoundedChoice(state, playerIdx, candidates[0]);
  }
  if (p.arianaTrigger?.stage === 'place') {
    const units = getUnitCardsInHand(p);
    if (units.length === 0) { p.arianaTrigger = null; return; }
    applyArianaTriggerPlaceChoice(state, playerIdx, { handCardInstanceId: units[0].instanceId });
  }
}

// === Активка Ф2: fill_line_with_summon ===
export function arianaActive(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.hero || p.hero.id !== HERO_ID) return { ok: false, reason: 'wrong_hero' };
  if (p.heroActiveUsedThisMatch) return { ok: false, reason: 'already_used' };

  let summoned = 0;
  for (let line = 0; line < 2; line++) {
    const row = p.field?.[line] || [];
    for (let s = 0; s < row.length; s++) {
      if (row[s] === null) {
        const u = summonUnit(state, playerIdx, MEDTEKH_ID, line, s);
        if (u) summoned++;
      }
    }
  }
  p.heroActiveUsedThisMatch = true;
  state.log?.add(`🩺 Ариана [${p.name}]: активка — призван ${summoned} Медтех(а) LL-1`);
  return { ok: true, summoned };
}

export function arianaActiveAvailable(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.hero || p.hero.id !== HERO_ID) return false;
  if (p.heroActiveUsedThisMatch) return false;
  if (p.hero.current?.silenced) return false;
  // Хотя бы одна свободная ячейка.
  for (let line = 0; line < 2; line++) {
    const row = p.field?.[line] || [];
    for (const s of row) if (s === null) return true;
  }
  return false;
}

registerImplementedAbility(HERO_ID, 'trigger', 'active');
